/**
 * Forgot Password Form Module
 * Collects an email and triggers a password-reset email.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForgotPassword } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { Spinner } from '@/components/ui/spinner';
import { Link } from 'react-router';

const forgotPasswordSchema = z.object({
  email: z.email('Please enter a valid email address').min(1, 'Email is required').max(254, 'Email is too long')
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const forgotPasswordMutation = useForgotPassword();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPasswordMutation.mutateAsync({ email: data.email });
      setSubmitted(true);
      toast.success('If an account exists for that email, a reset link has been sent.');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    }
  };

  if (submitted) {
    return (
      <div className='space-y-4 text-sm text-muted-foreground'>
        <p>
          If an account exists for <span className='font-medium text-foreground'>{form.getValues('email')}</span>,
          we&apos;ve sent a password-reset link. Check your inbox and follow the instructions.
        </p>
        <Link to='/internal-login' className='text-primary hover:underline'>
          Back to login
        </Link>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <p className='text-sm text-muted-foreground'>
          Enter your email and we&apos;ll send you a link to reset your password.
        </p>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type='email' placeholder='Enter your email' autoComplete='email' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type='submit' className='w-full' variant={'outline'} disabled={forgotPasswordMutation.isPending}>
          {forgotPasswordMutation.isPending ? (
            <>
              <Spinner className='mr-2' />
              Sending...
            </>
          ) : (
            'Send reset link'
          )}
        </Button>

        <div className='text-center text-sm text-muted-foreground'>
          <Link to='/internal-login' className='text-primary hover:underline'>
            Back to login
          </Link>
        </div>
      </form>
    </Form>
  );
}
